import { useEffect, useCallback, useRef, useState } from 'react';

const useWebSocket = (token) => {
  // Use refs to maintain consistent references and persist values between renders
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const handlersRef = useRef([]);
  const isConnectingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const manuallyClosedRef = useRef(false);
  const [isConnected, setIsConnected] = useState(false);

  // Create WebSocket connection
  const connect = useCallback(() => {
    if (!token) {
      console.log('No token available, skipping WebSocket connection');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN || isConnectingRef.current) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    manuallyClosedRef.current = false;
    isConnectingRef.current = true;
    
    try {
      // Clean up any existing connection
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      // Use same backend URL as API but with WebSocket protocol
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let wsUrl;
      if (backendUrl) {
        // If explicit backend URL is provided, use it
        wsUrl = backendUrl.replace('http', 'ws');
      } else {
        // Use current location for relative WebSocket connection (Kubernetes ingress)
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${protocol}//${window.location.host}`;
      }
      const newWs = new WebSocket(`${wsUrl}/ws?token=${token}`);
      wsRef.current = newWs;

      newWs.onopen = () => {
        if (wsRef.current !== newWs) return;
        console.log('WebSocket connected successfully');
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        isConnectingRef.current = false;
        reconnectAttemptsRef.current = 0;
        setIsConnected(true);

        // Send periodic ping to keep connection alive
        const pingInterval = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          } else {
            clearInterval(pingInterval);
          }
        }, 30000);
        wsRef.current.pingInterval = pingInterval;
      };

      newWs.onclose = (event) => {
        if (wsRef.current !== newWs) return;
        console.log('WebSocket disconnected', event.code, event.reason);
        isConnectingRef.current = false;
        setIsConnected(false);

        // Clear ping interval if it exists
        if (wsRef.current?.pingInterval) {
          clearInterval(wsRef.current.pingInterval);
        }

        // Define reconnection conditions
        const isAuthFailure = event.code === 1008; // Policy violation (usually auth)
        
        // A browser/proxy can report code 1001 after a short network or tab
        // suspension. Reconnect every non-auth closure unless this hook closed
        // the socket deliberately during logout/unmount.
        const shouldReconnect = !manuallyClosedRef.current && !isAuthFailure && token;
        
        // Clear any existing reconnection timer
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        
        if (shouldReconnect) {
          const attempt = reconnectAttemptsRef.current;
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(30000, 1000 * (2 ** attempt)) + Math.floor(Math.random() * 500);
          console.log('Scheduling WebSocket reconnection...');
          reconnectTimerRef.current = setTimeout(() => {
            console.log('Attempting to reconnect WebSocket...');
            reconnectTimerRef.current = null;
            connect();
          }, delay);
        }
      };

      newWs.onerror = (error) => {
        if (wsRef.current !== newWs) return;
        console.error('WebSocket error:', error);
        isConnectingRef.current = false;
        setIsConnected(false);
      };

      // Set up message handler
      newWs.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handlersRef.current.forEach(handler => handler(data));
        } catch (error) {
          console.error('Error processing WebSocket message:', error);
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      isConnectingRef.current = false;
    }

    // ws.onmessage = (event) => {
    //   try {
    //     const data = JSON.parse(event.data);
    //     // Notify all registered handlers
    //     handlersRef.current.forEach(handler => handler(data));
    //   } catch (error) {
    //     console.error('Error processing WebSocket message:', error);
    //   }
    // };

    // wsRef.current = newWs;
  }, [token]);

  // Subscribe to WebSocket messages
  const subscribe = useCallback((handler) => {
    handlersRef.current = [...handlersRef.current, handler];
    return () => {
      handlersRef.current = handlersRef.current.filter(h => h !== handler);
    };
  }, []);

  // Close WebSocket connection
  const close = useCallback(() => {
    manuallyClosedRef.current = true;
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    handlersRef.current = [];
    isConnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
  }, []);

  // Automatically connect when component mounts and clean up on unmount
  useEffect(() => {
    if (token) {
      connect();
    }
    return () => close();
  }, [token, connect, close]);

  // Browsers and reverse proxies may silently suspend an idle socket while a
  // tab is hidden. Re-establish it as soon as the user returns or connectivity
  // changes, rather than waiting for a manual page refresh.
  useEffect(() => {
    if (!token) return undefined;
    const reconnectIfNeeded = () => {
      if (document.visibilityState === 'hidden') return;
      if (wsRef.current?.readyState !== WebSocket.OPEN) connect();
    };
    window.addEventListener('online', reconnectIfNeeded);
    document.addEventListener('visibilitychange', reconnectIfNeeded);
    return () => {
      window.removeEventListener('online', reconnectIfNeeded);
      document.removeEventListener('visibilitychange', reconnectIfNeeded);
    };
  }, [token, connect]);

  // Return the WebSocket interface
  return {
    subscribe,
    close,
    connect,
    isConnected,
    reconnect: () => {
      close();
      connect();
    }
  };
};

export default useWebSocket;
